import React from "react";
import { StepShell } from "@/components/StepShell";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ASTIG_ESITI, ASTIG_AXES, ASTIG_CIRCLE_PX, astigmatismChartPx } from "@/lib/screeningData";
import { useI18n } from "@/i18n";
import { Radar } from "lucide-react";

// Un cerchio riempito da un reticolo di linee parallele all'angolo `deg`. Il reticolo è ritagliato
// dal cerchio con una `clipPath`: disegnare solo le corde interne calcolandone gli estremi darebbe
// lo stesso risultato con molta più aritmetica, e sbagliarla si vede solo ai bordi.
//
// Le linee si generano perpendicolarmente all'orientamento richiesto e poi si ruota l'intero
// gruppo: così il passo del reticolo resta esattamente `pitch` a qualunque angolo, mentre
// calcolando le linee già inclinate il passo si accorcerebbe di un coseno sulle oblique.
const GratingCircle = ({ deg, geom, x = 0, y = 0, id }) => {
  const r = geom.circle / 2;
  const cx = x + r;
  const cy = y + r;
  // Si copre il diametro partendo da un multiplo del passo, così i quattro cerchi restano in fase
  // fra loro e nessuno mostra una linea in più per un arrotondamento.
  const half = Math.ceil(r / geom.pitch) * geom.pitch;
  const offsets = [];
  for (let o = -half; o <= half + 1e-9; o += geom.pitch) offsets.push(o);

  return (
    <g data-testid={`astigmatism-circle-${deg}`}>
      <clipPath id={id}>
        <circle cx={cx} cy={cy} r={r - geom.ring / 2} />
      </clipPath>
      {/* Il reticolo è orizzontale nel sistema locale e viene ruotato di -deg: in SVG le rotazioni
          positive vanno in senso orario, i gradi ottici in senso antiorario. */}
      <g clipPath={`url(#${id})`} transform={`rotate(${-deg} ${cx} ${cy})`}>
        {offsets.map((o, i) => (
          <line key={i} x1={cx - r} y1={cy + o} x2={cx + r} y2={cy + o}
            stroke="#000" strokeWidth={geom.line} shapeRendering="crispEdges" />
        ))}
      </g>
      <circle cx={cx} cy={cy} r={r - geom.ring / 2} fill="none" stroke="#000" strokeWidth={geom.ring} />
    </g>
  );
};

// Tavola dell'astigmatismo: quattro cerchi 2×2, ciascuno con il reticolo a un orientamento diverso.
// Come la griglia di Amsler si disegna alla misura nominale e si restringe con il contenitore
// (`maxWidth: 100%`) — il viewBox conserva le proporzioni, quindi su schermi stretti la tavola
// rimpicciolisce invece di essere tagliata. Ridurla non invalida il test: il confronto fra i
// quattro riquadri non dipende dalla scala (vedi `astigmatismChartPx`).
const AstigmatismChart = ({ circlePx = ASTIG_CIRCLE_PX }) => {
  const geom = astigmatismChartPx(circlePx);
  const step = geom.circle + geom.gap;

  return (
    <svg width={geom.size} height={geom.size} viewBox={`0 0 ${geom.size} ${geom.size}`}
      style={{ background: "#fff", maxWidth: "100%", height: "auto" }} data-testid="astigmatism-chart">
      {ASTIG_AXES.map((deg, i) => (
        <GratingCircle key={deg} deg={deg} geom={geom} id={`astig-clip-${deg}`}
          x={(i % 2) * step} y={Math.floor(i / 2) * step} />
      ))}
    </svg>
  );
};

const EyePanel = ({ eye, label, esito, onEsito, tc }) => (
  <div className="rounded-md border border-slate-200 bg-white p-5">
    <p className="text-sm font-semibold text-slate-800 mb-3">{label}</p>
    <RadioGroup className="space-y-2" value={esito} onValueChange={onEsito}>
      {ASTIG_ESITI.map((e) => (
        <label key={e} htmlFor={`astigmatism-${eye}-${e}`}
          className={`flex items-center gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
            esito === e ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"
          }`}>
          <RadioGroupItem value={e} id={`astigmatism-${eye}-${e}`}
            data-testid={`astigmatism-${eye}-esito-${e.toLowerCase().replace(/\s+/g, "-")}`} />
          <span className="text-sm text-slate-700">{tc(e)}</span>
        </label>
      ))}
    </RadioGroup>
  </div>
);

export const AstigmatismTest = ({ data, update }) => {
  const { t, tc } = useI18n();
  const a = data.astigmatism;
  // `ou` è arrivato dopo: uno screening ripreso da `pendingReport` può non averlo, e il merge con
  // `initialData()` è superficiale — sostituisce l'intero oggetto `astigmatism`, non lo completa.
  const esitoOf = (eye) => (a[eye] || {}).esito || "";
  const setEye = (eye, esito) => update("astigmatism", { [eye]: { ...(a[eye] || {}), esito } });

  return (
    <StepShell icon={Radar} title={t("as_title")} subtitle={t("as_sub")} testId="astigmatism-step">
      <div className="mb-4 rounded-md border border-slate-200 bg-white p-4" data-testid="astigmatism-instructions">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("as_how")}</p>
        <ol className="mt-2 space-y-1.5 text-sm text-slate-700 list-decimal list-inside">
          <li>{t("as_step_od")}</li>
          <li>{t("as_step_os")}</li>
          <li>{t("as_step_ou")}</li>
        </ol>
        <p className="mt-3 text-xs text-slate-500">{t("as_interpretation")}</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="bg-white px-6 py-10 flex justify-center"><AstigmatismChart /></div>
        <div className="border-t border-slate-200 bg-slate-50 p-6">
          <p className="text-sm font-semibold text-slate-800 mb-3">{t("as_result")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <EyePanel eye="od" label={t("eye_od")} esito={esitoOf("od")} tc={tc} onEsito={(v) => setEye("od", v)} />
            <EyePanel eye="os" label={t("eye_os")} esito={esitoOf("os")} tc={tc} onEsito={(v) => setEye("os", v)} />
            <EyePanel eye="ou" label={t("eye_ou")} esito={esitoOf("ou")} tc={tc} onEsito={(v) => setEye("ou", v)} />
          </div>
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("notes")}</Label>
            <Textarea data-testid="astigmatism-note-input" rows={2} value={a.note}
              onChange={(e) => update("astigmatism", { note: e.target.value })} placeholder={t("notes_ph")} />
          </div>
        </div>
      </div>
    </StepShell>
  );
};
